import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useDonationContextProvider } from 'context/DonationContextProvider';
import { useBranchContext } from 'context/BranchContext';
import Payment from 'components/add-donation/Payment';
import { recoverPaymentSummary } from '../../lib/supabaseQueries';

import { formatMoney } from 'helpers/utils';
import { DONATION_STEPS } from './data';
import { DonationFooter } from './DonationFooter';
import { DonationHeader } from './DonationHeader';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { DonationItem } from './donor-information/donation-summary';

function DonationPaymentPage() {
  const { branchId } = useBranchContext();
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryError, setRecoveryError] = useState(null);

  const {
    setDonationStep,
    basket,
    setBasket,
    transactionFeeAmount,
    setTransactionFeeAmount,
    subtotal,
    totalAmount,
    transactionFee,
    setTransactionFee,
    clientSecret,
    stripeKey,
    setClientSecret,
    setStripeKey
  } = useDonationContextProvider();

  const location = useLocation();
  const baseUrl = window.location.origin;
  const pathParams = new URLSearchParams(window.location.search);
  const sessionId = pathParams.get('sessionId');
  const isRecoveryMode = !!sessionId;

  // Recovery flow - load session data from URL parameter
  useEffect(() => {
    console.log('Recovery useEffect - sessionId:', sessionId, 'branchId:', branchId);
    if (!sessionId || !branchId) return;

    const recoverSession = async () => {
      console.log('Starting recovery for session:', sessionId);
      setIsRecovering(true);
      setRecoveryError(null);

      try {
        const { data, error } = await recoverPaymentSummary(sessionId, branchId);

        if (error) {
          // Handle specific error messages
          if (error.includes('already been completed')) {
            setRecoveryError('This donation has already been completed. Thank you!');
            // Optionally redirect to thank you page
            setTimeout(() => {
              setDonationStep(DONATION_STEPS.THANKS);
            }, 2000);
          } else if (error.includes('expired')) {
            setRecoveryError('This checkout session has expired. Please start a new donation.');
          } else if (error.includes('not found')) {
            setRecoveryError('Session not found. Please start a new donation.');
          } else {
            setRecoveryError(error);
          }
          return;
        }

        if (data) {
          console.log('Recovery data:', data);

          if (data.basket) {
            setBasket(data.basket);
          }
          if (data.transactionFee !== undefined) {
            setTransactionFeeAmount(data.transactionFee);
            setTransactionFee(data.transactionFee > 0);
          }
          if (data.client_secret) {
            setClientSecret(data.client_secret);
          }
          if (data.publishable_key) {
            setStripeKey(data.publishable_key);
          }
        }
      } catch (err) {
        console.error('Failed to recover payment session:', err);
        setRecoveryError('Something went wrong. Please try again or start a new donation.');
      } finally {
        setIsRecovering(false);
      }
    };

    recoverSession();
  }, [sessionId, branchId, setBasket, setTransactionFeeAmount, setTransactionFee, setClientSecret, setStripeKey, setDonationStep]);

  // Show recovery loading state (also while waiting for branchId)
  if (isRecoveryMode && (isRecovering || !branchId)) {
    return (
      <div className="bg-wr-donation-grey-50">
        <DonationHeader />
        <div className="container py-6">
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Loading your donation...</p>
          </div>
        </div>
        <DonationFooter />
      </div>
    );
  }

  // Show recovery error state
  if (isRecoveryMode && recoveryError) {
    return (
      <div className="bg-wr-donation-grey-50">
        <DonationHeader />
        <div className="container py-6">
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
            <Alert variant="warning" className="text-center">
              <Alert.Heading>Unable to Load Session</Alert.Heading>
              <p>{recoveryError}</p>
              <hr />
              <Button
                variant="primary"
                onClick={() => setDonationStep(DONATION_STEPS.BASKET_CREATION)}
              >
                Start New Donation
              </Button>
            </Alert>
          </div>
        </div>
        <DonationFooter />
      </div>
    );
  }

  return (
    <div className="bg-wr-donation-grey-50">
      <DonationHeader />
      <div className="container py-6">
        <div className="d-flex align-items-center mb-3">
          {!isRecoveryMode && (
            <Button
              className="border-0 bg-wr-donation-grey-150 wr-donation-text-grey-800 py-2 px-3 me-3"
              onClick={() => setDonationStep(DONATION_STEPS.DONOR_INFORMATION)}
            >
              &lt; Back
            </Button>
          )}
          <h2 className="text-uppercase wr-donation-body3 mb-0 flex-grow-1 text-center">
            {isRecoveryMode ? 'Complete Your Donation' : 'Donation Summary'}
          </h2>
        </div>

        {basket.length > 0 ? (
          <>
            <div className="d-flex flex-column gap-3 mb-3">
              {basket.map(donation => (
                <DonationItem key={donation.id} donation={donation} />
              ))}
            </div>
            <header className="wr-donation-text-brand-secondary fw-bold d-flex justify-content-between align-items-center">
              <span>Subtotal</span>
              <span>{formatMoney(subtotal, '£')}</span>
            </header>
            <header className="wr-donation-text-brand-secondary fw-bold d-flex justify-content-between align-items-center">
              <span>Transaction Fee</span>
              <span>
                {formatMoney(transactionFee ? transactionFeeAmount : 0, '£')}
              </span>
            </header>
            <header className="wr-donation-text-brand-secondary fw-bold d-flex justify-content-between align-items-center">
              <span>Total</span>
              <span>{formatMoney(totalAmount, '£')}</span>
            </header>
          </>
        ) : (
          <Alert variant="info" className="text-center">
            No items in basket. Please add donations to continue.
          </Alert>
        )}

        <hr className="wr-donation-border-primary my-3" />
        <h2 className="text-uppercase wr-donation-body3 text-center mb-3">
          Payment Details
        </h2>
        <div className="d-flex flex-column gap-2 mt-4">
          <div>
            <h2 className="wr-donation-payment-method-header">Card Details</h2>
            {clientSecret && stripeKey ? (
              <Payment
                clientSecret={clientSecret}
                redirectUrl={`${baseUrl}${location.pathname}`}
                stripeKey={stripeKey}
              />
            ) : (
              <div className="payment-form-skeleton">
                <div className="mb-3">
                  <label className="form-label placeholder-glow">
                    <span className="placeholder col-4"></span>
                  </label>
                  <div className="placeholder-glow">
                    <span
                      className="placeholder col-12 bg-secondary"
                      style={{ height: '40px' }}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label placeholder-glow">
                      <span className="placeholder col-6" />
                    </label>
                    <div className="placeholder-glow">
                      <span
                        className="placeholder col-12 bg-secondary"
                        style={{ height: '40px' }}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label placeholder-glow">
                      <span className="placeholder col-4" />
                    </label>
                    <div className="placeholder-glow">
                      <span
                        className="placeholder col-12 bg-secondary"
                        style={{ height: '40px' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 placeholder-glow">
                  <span
                    className="placeholder col-12 bg-secondary"
                    style={{ height: '45px' }}
                  />
                </div>
              </div>
            )}
          </div>
          <p className="wr-donation-text-grey-700">
            Give with confidence. Our secure payment system ensures your personal
            details are kept safe and secure.
          </p>
        </div>
      </div>
      <DonationFooter />
    </div>
  );
}

export { DonationPaymentPage };
